import { IEmployeeAppInboxMoment } from '@dadohr/shared/domainTypes/experience';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import {
  ItemSorters,
  RowsRenderer,
  RowsRendererHeadProps,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { Centered } from '../../../../../../../layout/Centered';
import { useStringQueryParam } from '../../../../../../../routes';
import { ROUTES_V2 } from '../../../../../../routes';
import { PlainDate } from '../../../../Experience/components/Tasks';
import { EmployeeAppLoader } from '../../../components/EmployeeAppLoader';
import { InboxNavItemKey } from '../../../components/Nav/InboxNav';
import { useEmployeeAppArchivedMoments } from '../../../services/retrieval';
import { TASK_GROUP_TABLE_SORT_QUERY_PARAM } from './TaskGroupTable';

// TODO - this is virtually identical to MessageGroupTable and could be unified

type ColumnName = 'title' | 'sender' | 'received';
type D = IEmployeeAppInboxMoment;
type OtherProps = {
  userAccessToken: string;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedUsers?: string[] | null;
  selectedTemplates?: string[] | null;
  tsort?: string;
};
type Column = IColumn<D, ColumnName, OtherProps>;

const COLUMNS: Column[] = [
  {
    key: 'title',
    head: () => 'Subject',
    cell: (d) => d.templateTitle,
    align: 'left',
    sortable: true,
    width: 300,
    flexGrow: 2,
    noWrap: true
  },
  {
    key: 'sender',
    head: () => 'Sender',
    cell: (d) => d.from.name || d.from.email,
    align: 'left',
    sortable: true,
    width: 300,
    flexGrow: 1,
    noWrap: true
  },
  {
    key: 'received',
    head: () => 'Received',
    cell: (d) => <PlainDate ts={d.sentAt} />,
    align: 'right',
    sortable: true,
    width: 120,
    flexGrow: 0
  }
];

export const MOMENT_TABLE_SORT_GETTERS: Record<
  string,
  (d: IEmployeeAppInboxMoment) => string | number
> = {
  title: (d) => d.templateTitle,
  sender: (d) => d.from.name || d.from.email,
  received: (d) => d.sentAt.toMillis()
};

const SORTERS: ItemSorters<D, OtherProps> = {
  title: {
    key: 'title',
    items: { sort: (d) => MOMENT_TABLE_SORT_GETTERS.title(d), dir: 'asc' }
  },
  sender: {
    key: 'sender',
    items: { sort: (d) => MOMENT_TABLE_SORT_GETTERS.sender(d), dir: 'asc' }
  },
  received: {
    key: 'received',
    items: { sort: (d) => MOMENT_TABLE_SORT_GETTERS.received(d), dir: 'desc' }
  }
};

const MOMENT_TABLE_DEFAULT_SORTER = SORTERS.received;

export const sortMoments = (
  moments: IEmployeeAppInboxMoment[],
  key: string | null | undefined,
  dir: 'asc' | 'desc'
) => {
  const sorter =
    MOMENT_TABLE_SORT_GETTERS[key || ''] ||
    MOMENT_TABLE_SORT_GETTERS[MOMENT_TABLE_DEFAULT_SORTER.key];
  return orderBy(moments, (d) => sorter(d), dir);
};

const ROW_TO_KEY = (d: D) => d.momentAccessToken;

const ROW_TO_HREF = (d: D, o: OtherProps) => {
  return ROUTES_V2.inboxMoment.url(o.userAccessToken, d.momentAccessToken, {
    returnTo: o.returnTo,
    g: o.selectedGroup || undefined,
    u: o.selectedUsers,
    t: o.selectedTemplates,
    tsort: o.tsort
  });
};

const HEAD_PROPS: RowsRendererHeadProps = {
  transparent: true,
  noTopPadding: true
};

export const MomentsTable = ({
  moments,
  userAccessToken,
  returnTo,
  selectedGroup,
  selectedUsers,
  selectedTemplates
}: {
  moments: IEmployeeAppInboxMoment[];
  userAccessToken: string;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedTemplates?: string[] | null;
  selectedUsers?: string[] | null;
}) => {
  const [[sorter, dir], setSort] = useSortQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    SORTERS
  );
  const [rawSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );
  const otherProps = useMemo<OtherProps>(() => {
    return {
      userAccessToken,
      returnTo,
      selectedGroup,
      selectedUsers,
      selectedTemplates,
      tsort: rawSortParam
    };
  }, [
    userAccessToken,
    returnTo,
    selectedGroup,
    selectedUsers,
    selectedTemplates,
    rawSortParam
  ]);
  const sorterOrDefault = sorter || MOMENT_TABLE_DEFAULT_SORTER;
  return (
    <RowsRenderer
      fullWidth
      variant="contained"
      columns={COLUMNS}
      rows={moments}
      rowToKey={ROW_TO_KEY}
      rowToHref={ROW_TO_HREF}
      sorter={sorterOrDefault}
      sortDirection={dir}
      renderHead={true}
      headProps={HEAD_PROPS}
      onHeadClick={(c, d) =>
        setSort([SORTERS[c.key] || MOMENT_TABLE_DEFAULT_SORTER, d])
      }
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};

export const MomentsArchivedTable = ({
  moments: currentMomentsMarkedAsRead,
  userAccessToken,
  returnTo,
  selectedGroup,
  selectedUsers,
  selectedTemplates,
  emptyState
}: {
  moments: IEmployeeAppInboxMoment[];
  userAccessToken: string;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedTemplates?: string[] | null;
  selectedUsers?: string[] | null;
  emptyState: React.ReactNode;
}) => {
  // We never need to re-retrieve these once they are cached.
  // If they are archived for time based reasons, they cannot be unarchived anyway.
  // All those where it's possible to interact with their marked-as-read state, are coming
  // from the regular inbox data, which is updating correctly.
  const [archivedMoments, loading] = useEmployeeAppArchivedMoments(
    userAccessToken
  );

  const combinedArchivedMoments = useMemo(() => {
    if (!archivedMoments) {
      // still loading
      return null;
    }
    // refilter what's coming in from the outside to be sure
    return [
      ...archivedMoments,
      ...currentMomentsMarkedAsRead.filter((m) => m.markedAsRead)
    ];
  }, [archivedMoments, currentMomentsMarkedAsRead]);

  if (loading || !combinedArchivedMoments) {
    return (
      <Centered height={500}>
        <EmployeeAppLoader height={Math.min(200, (500 / 3) * 2)} />
      </Centered>
    );
  }

  if (!combinedArchivedMoments.length) {
    return <>{emptyState}</>;
  }

  return (
    <MomentsTable
      moments={combinedArchivedMoments}
      userAccessToken={userAccessToken}
      returnTo={returnTo}
      selectedGroup={selectedGroup}
      selectedUsers={selectedUsers}
      selectedTemplates={selectedTemplates}
    />
  );
};
